import React, { useState, useEffect } from 'react';
import { Box, TextField, Checkbox, FormControlLabel, IconButton, Icon, Chip, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Button } from 'global/components/Button';
import { get, size } from 'global/lib/lodash';
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const AVAILABLE_SECTIONS = [
  { name: 'info', label: 'Info', defaultEnabled: true },
  { name: 'score_summary', label: 'Score Summary', defaultEnabled: true },
  { name: 'highlights', label: 'Highlights', defaultEnabled: true },
  { name: 'scores', label: 'Scores', defaultEnabled: true },
  { name: 'tasks', label: 'Tasks', defaultEnabled: false },
];

const TIER_OPTIONS = ['division', 'community', 'lot'];

const OptionCard = styled('div')(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 4,
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  position: 'relative',
}));

const RemoveBtn = styled(IconButton)({
  position: 'absolute',
  top: 4,
  right: 4,
});

const CardHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1),
  paddingRight: 36,
}));

const TierRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

const SectionsLabel = styled('div')(({ theme }) => ({
  fontWeight: 'bold',
  fontSize: 13,
  marginBottom: 4,
  color: theme.palette.text.secondary,
}));

const SectionRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '2px 0',
  borderRadius: 4,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const ChipContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 4,
  marginBottom: theme.spacing(1),
}));

const dragHandleSx = {
  cursor: 'grab',
  color: 'text.disabled',
  display: 'flex',
  alignItems: 'center',
  mr: '4px',
};

const orderNumSx = {
  fontSize: 12,
  color: 'text.secondary',
  minWidth: 20,
};

const ensureAllSections = (sections) => {
  const existing = Array.isArray(sections) ? sections : [];
  const maxOrder = existing.reduce((max, s) => Math.max(max, s.order || 0), 0);
  const result = [...existing];
  AVAILABLE_SECTIONS.forEach((as, i) => {
    if (!result.find((s) => s.name === as.name)) {
      result.push({ name: as.name, order: maxOrder + i + 1, enabled: as.defaultEnabled });
    }
  });
  return result;
};

const DEFAULT_OPTION = {
  name: 'Print Assessment',
  tier: [],
  sections: AVAILABLE_SECTIONS.map((s, i) => ({
    name: s.name,
    order: i + 1,
    enabled: s.defaultEnabled,
  })),
};

const SortableSection = ({ section, index, label, onToggleEnabled }) => {
  const theme = useTheme();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.name });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    backgroundColor: isDragging ? theme.palette.action.hover : undefined,
  };
  return (
    <SectionRow ref={setNodeRef} style={style} {...attributes}>
      <Box component="span" {...listeners} sx={{ ...dragHandleSx, touchAction: 'none' }}>
        <Icon fontSize="small">drag_indicator</Icon>
      </Box>
      <FormControlLabel
        control={
          <Checkbox
            checked={section.enabled !== false}
            onChange={(e) => onToggleEnabled(e.target.checked)}
            size="small"
          />
        }
        label={label}
      />
    </SectionRow>
  );
};

const PrintOptionsEditor = ({ value, onChange, disabled }) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const [options, setOptions] = useState(() => {
    if (Array.isArray(value) && size(value)) {
      return value.map((opt) => ({ ...opt, sections: ensureAllSections(opt.sections) }));
    }
    return [{ ...DEFAULT_OPTION }];
  });

  useEffect(() => {
    if (Array.isArray(value) && size(value)) {
      setOptions(value.map((opt) => ({ ...opt, sections: ensureAllSections(opt.sections) })));
    }
  }, [value]);

  const update = (newOptions) => {
    setOptions(newOptions);
    onChange && onChange(newOptions);
  };

  const updateOption = (index, key, val) => {
    const updated = [...options];
    updated[index] = { ...updated[index], [key]: val };
    update(updated);
  };

  const addOption = () => {
    update([...options, {
      name: '',
      tier: [],
      sections: AVAILABLE_SECTIONS.map((s, i) => ({
        name: s.name,
        order: i + 1,
        enabled: s.defaultEnabled,
      })),
    }]);
  };

  const removeOption = (index) => {
    const updated = options.filter((_, i) => i !== index);
    update(size(updated) ? updated : [{ ...DEFAULT_OPTION }]);
  };

  const toggleSectionEnabled = (optIndex, sectionName, enabled) => {
    const updated = [...options];
    const sections = [...get(updated[optIndex], 'sections', [])];
    const idx = sections.findIndex((s) => s.name === sectionName);
    if (idx >= 0) {
      sections[idx] = { ...sections[idx], enabled };
      updated[optIndex] = { ...updated[optIndex], sections };
      update(updated);
    }
  };

  const onDragEnd = (optIndex, event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const updated = [...options];
    const sections = [...get(updated[optIndex], 'sections', [])].sort((a, b) => a.order - b.order);
    const oldIndex = sections.findIndex((s) => s.name === active.id);
    const newIndex = sections.findIndex((s) => s.name === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const moved = arrayMove(sections, oldIndex, newIndex);
    const reordered = moved.map((s, i) => ({ ...s, order: i + 1 }));
    updated[optIndex] = { ...updated[optIndex], sections: reordered };
    update(updated);
  };

  const toggleTier = (optIndex, tier) => {
    const current = get(options[optIndex], 'tier', []);
    const has = current.includes(tier);
    const newTiers = has ? current.filter((t) => t !== tier) : [...current, tier];
    updateOption(optIndex, 'tier', newTiers);
  };

  // Name is only shown to users on the assessment page when multiple print
  // configs exist (single config → button just says "Print"), so don't let
  // users edit a name that won't be visible.
  const nameDisabled = disabled || size(options) <= 1;

  return (
    <Box sx={{ width: '100%' }}>
      {options.map((opt, optIndex) => {
        const sorted = [...(get(opt, 'sections', []) || [])].sort((a, b) => a.order - b.order);
        return (
          <OptionCard key={optIndex}>
            {!disabled && size(options) > 1 && (
              <RemoveBtn size="small" onClick={() => removeOption(optIndex)}>
                <Icon>close</Icon>
              </RemoveBtn>
            )}
            <CardHeader>
              <TextField
                size="small"
                variant={nameDisabled ? 'standard' : 'outlined'}
                label="Name"
                value={get(opt, 'name', '')}
                onChange={(e) => updateOption(optIndex, 'name', e.target.value)}
                disabled={nameDisabled}
                helperText={!disabled && size(options) <= 1 ? 'Add another option to enable naming' : undefined}
                style={{ flex: 1 }}
              />
            </CardHeader>

            <TierRow>
              <Typography variant="caption" style={{ fontWeight: 'bold', minWidth: 40 }}>Tier:</Typography>
              <ChipContainer>
                {!size(get(opt, 'tier', [])) && <Chip label="All" size="small" variant="outlined" />}
                {TIER_OPTIONS.map((tier) => {
                  const selected = (get(opt, 'tier', []) || []).includes(tier);
                  return (
                    <Chip
                      key={tier}
                      label={tier.charAt(0).toUpperCase() + tier.slice(1)}
                      size="small"
                      color={selected ? 'primary' : 'default'}
                      variant={selected ? 'filled' : 'outlined'}
                      onClick={disabled ? undefined : () => toggleTier(optIndex, tier)}
                      disabled={disabled}
                    />
                  );
                })}
              </ChipContainer>
            </TierRow>

            <SectionsLabel>Sections:</SectionsLabel>
            {disabled ? (
              sorted.map((section, i) => {
                const label = get(AVAILABLE_SECTIONS.find((s) => s.name === section.name), 'label', section.name);
                return (
                  <SectionRow key={section.name}>
                    <Box component="span" sx={orderNumSx}>{i + 1}.</Box>
                    <FormControlLabel
                      control={<Checkbox checked={section.enabled !== false} size="small" disabled />}
                      label={label}
                    />
                  </SectionRow>
                );
              })
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={(event) => onDragEnd(optIndex, event)}>
                <SortableContext items={sorted.map((s) => s.name)} strategy={verticalListSortingStrategy}>
                  <div>
                    {sorted.map((section, i) => {
                      const label = get(AVAILABLE_SECTIONS.find((s) => s.name === section.name), 'label', section.name);
                      return (
                        <SortableSection
                          key={section.name}
                          section={section}
                          index={i}
                          label={label}
                          onToggleEnabled={(enabled) => toggleSectionEnabled(optIndex, section.name, enabled)}
                        />
                      );
                    })}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </OptionCard>
        );
      })}
      {!disabled && (
        <Button variant="outlined" onClick={addOption} startIcon={<Icon>add</Icon>}>
          Add Print Option
        </Button>
      )}
    </Box>
  );
};

export default PrintOptionsEditor;
